import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Menu, X, ChevronDown,
  Building2, DollarSign, User, LogOut, Settings,
  MessageCircle, BookOpen, CalendarCheck,
  HelpCircle, Plane, PlaySquare, Activity, LayoutGrid, LifeBuoy,
} from 'lucide-react';
import { Button } from './ui/button';
import { TreatmentsMegaMenu, PatientSupportMenu, ForClinicsMenu, UrgentCareMenu, MobileMenuGroup } from './HeaderMenus';
import { TREATMENT_SERVICES, PATIENT_SUPPORT, FOR_CLINICS, URGENT_CARE_MOBILE } from '../data/navigation';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { useLocalizedNavigate, buildLocalizedPath } from './LocaleRouter';
import { useCurrency } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { useContactInfo, getWhatsAppHref } from '../contexts/ContactInfoContext';

/**
 * Premium VietClinics header — healthcare-marketplace style.
 *
 *   Desktop layout:
 *     [ Logo ]   Treatments  How It Works  Travel Hub  Guides  For Clinics  Download App
 *                                                                          [ US$ ▾ ] [ EN ▾ ] [ Request Treatment ]
 *
 *   Mobile (≤ lg):  same items collapsed into a hamburger sheet.
 *
 * Behaviour:
 *   • Sticky white nav with soft bottom border + glass-blur once scrolled.
 *   • Language: EN + VN visible (full 9-locale list still available via the
 *     language router for i18n urls, but the surface stays minimal per spec).
 *   • Currency: US$ + VND only — preference persisted via CurrencyContext.
 *   • Every menu item maps to a real route — no dead links.
 */

// Marketplace-visible language surface. The full LANGUAGES list still drives
// i18n; this just keeps the dropdown minimal as the user requested.
const PRIMARY_LANG_CODES = ['en', 'vi'];
const labelForLang = (code) => (code === 'vi' ? 'VN' : code.toUpperCase());

const labelForCurrency = (curr) => (curr.code === 'USD' ? 'US$' : curr.code);

const Header = () => {
  const { language, changeLanguage, t } = useLanguage();
  const localizedNavigate = useLocalizedNavigate();
  const { currency, changeCurrency, currencies, currentCurrency } = useCurrency();
  const { user, logout } = useAuth();
  const { contactInfo } = useContactInfo();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const currencyDropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);

  const adminEmail = process.env.REACT_APP_ADMIN_EMAIL;
  const isAdmin = user && (
    user.role === 'admin'
    || user.isAdmin === true
    || (adminEmail && user.email === adminEmail)
  );

  // Glass-blur effect once the user scrolls past 8px.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close any open menu on route change.
  useEffect(() => {
    setMobileMenuOpen(false);
    setCurrencyDropdownOpen(false);
    setLanguageDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdowns on outside click.
  useEffect(() => {
    const handler = (e) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target)) setCurrencyDropdownOpen(false);
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(e.target)) setLanguageDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const whatsappHref = getWhatsAppHref(contactInfo);

  // Filter currencies to the user-visible surface (USD + VND).
  const visibleCurrencies = currencies.filter((c) => c.code === 'USD' || c.code === 'VND');
  // Filter languages to EN + VN visible (always include current even if not in primary).
  const visibleLanguages = LANGUAGES.filter((l) => PRIMARY_LANG_CODES.includes(l.code));

  // Build the primary nav once so desktop + mobile stay in sync.
  const NAV_ITEMS = [
    { kind: 'urgent',  label: 'Urgent Care', testid: 'nav-urgent-care', icon: Activity, items: URGENT_CARE_MOBILE, activePaths: ['/urgent-medical-care', '/doctor-to-hotel-vietnam', '/english-speaking-doctor-vietnam'] },
    { kind: 'mega',    label: 'Treatments & Services', testid: 'nav-treatments-services', icon: LayoutGrid, items: TREATMENT_SERVICES, activePaths: ['/treatments', '-in-vietnam', '/health-checkup-packages'] },
    { kind: 'support', label: 'Patient Support', testid: 'nav-patient-support', icon: LifeBuoy, items: PATIENT_SUPPORT, activePaths: PATIENT_SUPPORT.map((i) => i.to.split('#')[0]) },
    { kind: 'link',    to: '/how-it-works',        label: 'How It Works',  testid: 'nav-how-it-works',  icon: HelpCircle   },
    { kind: 'link',    to: '/travel-hub',          label: 'Travel Hub',    testid: 'nav-travel-hub',    icon: Plane        },
    { kind: 'link',    to: '/guides',              label: 'Guides',        testid: 'nav-guides',        icon: BookOpen     },
    { kind: 'link',    to: '/media',               label: 'Media',         testid: 'nav-media',         icon: PlaySquare   },
    { kind: 'clinics', label: 'For Clinics', testid: 'nav-for-clinics', icon: Building2, items: FOR_CLINICS, activePaths: ['/clinic-registration', '/pricing', '/for-clinics'] },
  ];
  const isActive = (paths) => paths.some((p) => location.pathname.includes(p));

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-[0_4px_24px_rgba(15,42,85,0.06)]'
          : 'bg-white border-b border-slate-100'
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto w-full max-w-[1600px] px-4">
        <div className="flex h-[64px] items-center gap-2">
          {/* ── Logo + Tagline ─────────────────────────────────────────── */}
          <Link
            to={buildLocalizedPath('/', language)}
            className="flex items-center flex-shrink-0 mr-3 group"
            data-testid="header-logo-link"
            aria-label="VietClinics — Smart Healthcare Made Simple"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_5a81bd9f-a300-47ec-b521-267995465c4c/artifacts/jwqlspi4_vietclinics-logo.png"
              alt="VietClinics"
              className="h-[44px] sm:h-[48px] md:h-[52px] xl:h-[44px] w-auto transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </Link>

          {/* ── Left-aligned primary nav ──────────────────────────────── */}
          <nav className="hidden xl:flex items-center gap-0.5 flex-1 whitespace-nowrap" data-testid="header-main-nav">
            {NAV_ITEMS.map((item) => {
              if (item.kind === 'mega') return <TreatmentsMegaMenu key={item.testid} active={isActive(item.activePaths)} />;
              if (item.kind === 'urgent') return <UrgentCareMenu key={item.testid} active={isActive(item.activePaths)} />;
              if (item.kind === 'support') return <PatientSupportMenu key={item.testid} active={isActive(item.activePaths)} />;
              if (item.kind === 'clinics') return <ForClinicsMenu key={item.testid} active={isActive(item.activePaths)} />;
              return (
                <NavLink
                  key={item.to}
                  to={buildLocalizedPath(item.to, language)}
                  testid={item.testid}
                  active={location.pathname.includes(item.to)}
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* ── Right side: currency · language · CTA ─────────────────── */}
          <div className="hidden xl:flex items-center gap-1.5 flex-shrink-0">
            {/* Currency — secondary control, shown on wide desktops */}
            <div className="relative hidden min-[1440px]:block" ref={currencyDropdownRef}>
              <button
                onClick={() => { setCurrencyDropdownOpen((o) => !o); setLanguageDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-2.5 h-8 text-[12.5px] font-semibold text-slate-700 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="currency-selector-btn"
                aria-haspopup="listbox"
                aria-expanded={currencyDropdownOpen}
              >
                {labelForCurrency(currentCurrency)}
                <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${currencyDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {currencyDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50">
                  {visibleCurrencies.map((curr) => (
                    <button
                      key={curr.code}
                      onClick={() => { changeCurrency(curr.code); setCurrencyDropdownOpen(false); }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        currency === curr.code ? 'bg-[#1FA7A1]/10 text-[#1FA7A1]' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`currency-option-${curr.code}`}
                    >
                      <span className="w-10 text-left font-bold">{labelForCurrency(curr)}</span>
                      <span className="text-xs text-slate-500">{curr.name}</span>
                      {currency === curr.code && <span className="ml-auto text-[#1FA7A1]">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Language — secondary control, shown on wide desktops */}
            <div className="relative hidden min-[1440px]:block" ref={languageDropdownRef}>
              <button
                onClick={() => { setLanguageDropdownOpen((o) => !o); setCurrencyDropdownOpen(false); }}
                className="flex items-center gap-1.5 px-2.5 h-8 text-[12.5px] font-semibold text-slate-700 rounded-full hover:bg-slate-100 transition-colors"
                data-testid="language-selector-btn"
                aria-haspopup="listbox"
                aria-expanded={languageDropdownOpen}
              >
                {labelForLang(language)}
                <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {languageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-xl border border-slate-100 py-2 z-50">
                  {visibleLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        localizedNavigate(lang.code);
                        changeLanguage(lang.code);
                        try { localStorage.setItem('vc_language', lang.code); } catch (_) { /* ignore */ }
                        setLanguageDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        language === lang.code ? 'bg-[#1FA7A1]/10 text-[#1FA7A1]' : 'text-slate-700 hover:bg-slate-50'
                      }`}
                      data-testid={`language-option-${lang.code}`}
                    >
                      <span className="text-base">{lang.flag}</span>
                      <p className="font-semibold">{labelForLang(lang.code)}</p>
                      <span className="text-xs text-slate-500 ml-1">{lang.nativeName}</span>
                      {language === lang.code && <span className="ml-auto text-[#1FA7A1]">✓</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Profile (signed-in users) */}
            {user && (
              <div className="relative group">
                <Link
                  to={buildLocalizedPath('/profile', language)}
                  className="flex items-center gap-1.5 text-slate-700 hover:text-[#1FA7A1] text-[13px] font-semibold px-2"
                  data-testid="header-profile-link"
                >
                  <User className="h-4 w-4" />
                  {user.name?.split(' ')[0] || t('nav.profile')}
                </Link>
                {isAdmin && (
                  <div className="absolute right-0 top-full pt-2 hidden group-hover:block z-50">
                    <div className="bg-white rounded-xl shadow-lg border border-slate-100 py-2 min-w-[180px]">
                      <Link
                        to={buildLocalizedPath('/admin/email-settings', language)}
                        className="flex items-center gap-3 px-4 py-2 text-sm text-[#1FA7A1] font-medium hover:bg-[#1FA7A1]/5"
                        data-testid="admin-email-settings-link"
                      >
                        <Settings className="h-4 w-4" /> {t('nav.emailSettings')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4" /> {t('nav.logout')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Primary CTA — Request Treatment */}
            <Link
              to={buildLocalizedPath('/request-treatment', language)}
              className="ml-1 inline-flex items-center gap-1.5 text-white font-bold rounded-full px-4 h-9 text-[12.5px] transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                boxShadow: '0 8px 20px rgba(31,167,161,0.28)',
              }}
              data-testid="header-request-treatment-btn"
            >
              <CalendarCheck className="h-4 w-4" />
              Request Treatment
            </Link>
          </div>

          {/* ── Mobile menu trigger ───────────────────────────────────── */}
          <button
            className="xl:hidden ml-auto p-2 text-slate-700 hover:bg-slate-100 rounded-lg"
            onClick={() => setMobileMenuOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* ── Mobile sheet ────────────────────────────────────────────────── */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-white border-t border-slate-100" data-testid="mobile-menu">
          <div className="container mx-auto px-4 py-4">
            {/* Language + Currency chips */}
            <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-slate-100">
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Language</p>
                <div className="flex gap-2">
                  {visibleLanguages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        localizedNavigate(lang.code);
                        changeLanguage(lang.code);
                        try { localStorage.setItem('vc_language', lang.code); } catch (_) { /* ignore */ }
                      }}
                      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold transition-colors ${
                        language === lang.code ? 'bg-[#1FA7A1] text-white' : 'bg-slate-100 text-slate-700'
                      }`}
                      data-testid={`mobile-language-${lang.code}`}
                    >
                      <span>{lang.flag}</span>
                      <span>{labelForLang(lang.code)}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Currency</p>
                <div className="flex gap-2">
                  {visibleCurrencies.map((curr) => (
                    <button
                      key={curr.code}
                      onClick={() => changeCurrency(curr.code)}
                      className={`flex-1 flex items-center justify-center px-3 py-2 rounded-full text-xs font-bold transition-colors ${
                        currency === curr.code ? 'bg-[#1FA7A1] text-white' : 'bg-slate-100 text-slate-700'
                      }`}
                      data-testid={`mobile-currency-${curr.code}`}
                    >
                      {labelForCurrency(curr)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <nav className="space-y-1" data-testid="mobile-main-nav">
              {NAV_ITEMS.map(({ kind, to, label, testid, icon: Icon, items }) => (
                kind === 'link' ? (
                  <MobileNavLink
                    key={to}
                    to={buildLocalizedPath(to, language)}
                    onClick={closeMobileMenu}
                    icon={<Icon className="h-5 w-5" />}
                    testid={`mobile-${testid}`}
                  >
                    {label}
                  </MobileNavLink>
                ) : (
                  <MobileMenuGroup
                    key={testid}
                    label={label}
                    icon={<Icon className="h-5 w-5" />}
                    items={items}
                    testid={`mobile-${testid}`}
                    onNavigate={closeMobileMenu}
                  />
                )
              ))}

              <Link
                to={buildLocalizedPath('/request-treatment', language)}
                onClick={closeMobileMenu}
                className="mt-4 flex items-center justify-center gap-2 text-white font-bold rounded-full py-3 transition-all"
                style={{
                  background: 'linear-gradient(135deg, #1FA7A1 0%, #178f8a 100%)',
                  boxShadow: '0 8px 20px rgba(31,167,161,0.28)',
                }}
                data-testid="mobile-request-treatment-btn"
              >
                <CalendarCheck className="h-4 w-4" />
                Request Treatment
              </Link>

              {whatsappHref && (
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-center gap-2 text-slate-700 font-semibold rounded-full py-3 border border-slate-200 hover:bg-slate-50 transition-colors"
                  data-testid="mobile-whatsapp-chat"
                >
                  <MessageCircle className="h-4 w-4" />
                  Chat on WhatsApp
                </a>
              )}
            </nav>

            {user && (
              <>
                <hr className="my-4 border-slate-100" />
                <div className="space-y-2">
                  <Link to={buildLocalizedPath('/profile', language)} onClick={closeMobileMenu}>
                    <Button variant="outline" className="w-full justify-start">
                      <User className="h-5 w-5 mr-2" /> My Profile
                    </Button>
                  </Link>
                  {isAdmin && (
                    <Link to={buildLocalizedPath('/admin/email-settings', language)} onClick={closeMobileMenu}>
                      <Button
                        variant="outline"
                        className="w-full justify-start border-[#1FA7A1] text-[#1FA7A1]"
                        data-testid="mobile-admin-settings-link"
                      >
                        <Settings className="h-5 w-5 mr-2" /> Email Settings
                      </Button>
                    </Link>
                  )}
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600"
                    onClick={() => { handleLogout(); closeMobileMenu(); }}
                  >
                    <LogOut className="h-5 w-5 mr-2" /> Logout
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

/* ── Sub-components ──────────────────────────────────────────────────────── */
const NavLink = ({ to, children, testid, active }) => (
  <Link
    to={to}
    className={`px-2 py-1.5 text-[13px] font-semibold rounded-full transition-all duration-200 ease-out ${
      active
        ? 'text-[#1FA7A1] bg-[#1FA7A1]/[0.08]'
        : 'text-slate-700 hover:text-[#1FA7A1] hover:bg-[#1FA7A1]/[0.06]'
    }`}
    data-testid={testid}
  >
    {children}
  </Link>
);

const MobileNavLink = ({ to, children, onClick, icon, testid }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-3 px-3 py-3 text-slate-800 hover:bg-slate-50 rounded-lg transition-colors font-semibold"
    data-testid={testid}
  >
    <span className="text-[#1FA7A1]">{icon}</span>
    {children}
  </Link>
);

export default Header;
